import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import axios from "axios";

const AddGroup = () => {
  const { accessToken } = useAuth();
  const location = useLocation();
  const { siteId } = location.state || {};
  const baseURL = process.env.REACT_APP_BASEURL;

  // State
  const [groupName, setGroupName] = useState("");
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch sites and user data
  useEffect(() => {
    const fetchSitesAndUser = async () => {
      setLoadingSites(true);
      setError(null);
      try {
        // Fetch sites
        const siteRes = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const siteList = siteRes.data.data || [];

        // Fetch user data
        const userRes = await axios.get(`${baseURL}/users/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userRes.data[0] || {};
        const userSiteIds = userData.sites || [];

        // Filter user-assigned sites
        const userSites = siteList.filter((site) =>
          userSiteIds.includes(site.id)
        );

        setSites(userSites);
        // Set initial site: use siteId from location.state if valid, else first site
        const initialSite = userSites.some((site) => site.id === siteId)
          ? siteId
          : userSites.length > 0
          ? userSites[0].id
          : "";
        setSelectedSite(initialSite);
      } catch (err) {
        setError("Failed to load sites. Please try again.");
        console.error("Sites fetch error:", err.response ? err.response.data : err.message);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchSitesAndUser();
  }, [accessToken, baseURL, siteId]);

  // Fetch devices when selectedSite changes
  useEffect(() => {
    const fetchDevices = async () => {
      if (!selectedSite) {
        setDevices([]);
        setSelectedDevices([]);
        return;
      }
      setLoadingDevices(true);
      setError(null);
      try {
        const response = await axios.get(`${baseURL}/devices/?site=${selectedSite}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const deviceList = response.data || [];
        const filteredDevices = deviceList.filter(
          (device) =>
            device.device_type &&
            !["base", "echo"].includes(device.device_type.toLowerCase())
        );
        setDevices(filteredDevices);
        setSelectedDevices([]); // Clear selected devices when site changes
      } catch (err) {
        setError("Failed to load devices. Please try again.");
        console.error("Devices fetch error:", err.response ? err.response.data : err.message);
      } finally {
        setLoadingDevices(false);
      }
    };

    fetchDevices();
  }, [accessToken, baseURL, selectedSite]);

  // Handle site selection
  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoadingSubmit(true);

    // Validate inputs
    if (!groupName.trim()) {
      setError("Group name is required.");
      setLoadingSubmit(false);
      return;
    }
    if (selectedDevices.length === 0) {
      setError("At least one device must be selected.");
      setLoadingSubmit(false);
      return;
    }
    if (!selectedSite) {
      setError("A site must be selected.");
      setLoadingSubmit(false);
      return;
    }

    try {
      const groupData = {
        name: groupName,
        devices: selectedDevices,
        site: selectedSite,
      };
      await axios.post(`${baseURL}/groups/`, groupData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setSuccess("Group created successfully!");
      // Reset form
      setGroupName("");
      setSelectedDevices([]);
    } catch (err) {
      setError("Failed to create group. Please try again.");
      console.error("Group creation error:", err.response ? err.response.data : err.message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Handle device selection
  const handleDeviceChange = (event) => {
    setSelectedDevices(event.target.value);
  };

  // Get site display name
  const getSiteDisplayName = (site) => {
    return site.name || `Site ${site.id}`;
  };

  // Get device display name
  const getDeviceDisplayName = (device) => {
    return device.name || `Device ${device.id}`;
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
          Add New Group
        </Typography>
        <FormControl
          variant="outlined"
          sx={{ minWidth: { xs: "100%", sm: 200 }, width: { xs: "100%", sm: "auto" } }}
          disabled={loadingSites}
        >
          <InputLabel id="site-select-label">Site</InputLabel>
          <Select
            labelId="site-select-label"
            value={selectedSite}
            onChange={handleSiteChange}
            label="Site"
          >
            {loadingSites ? (
              <MenuItem value="" disabled>
                Loading sites...
              </MenuItem>
            ) : sites.length === 0 ? (
              <MenuItem value="" disabled>
                No sites available
              </MenuItem>
            ) : (
              sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {getSiteDisplayName(site)}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Group Name */}
          <TextField
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            required
            variant="outlined"
          />
          {/* Devices Dropdown */}
          <FormControl fullWidth variant="outlined" disabled={loadingDevices || !selectedSite}>
            <InputLabel id="devices-select-label">Devices</InputLabel>
            <Select
              labelId="devices-select-label"
              value={selectedDevices}
              onChange={handleDeviceChange}
              label="Devices"
              multiple
              renderValue={(selected) =>
                selected
                  .map((deviceId) => {
                    const device = devices.find((d) => d.id === deviceId);
                    return device ? getDeviceDisplayName(device) : "";
                  })
                  .join(", ")
              }
            >
              {loadingDevices ? (
                <MenuItem value="" disabled>
                  Loading devices...
                </MenuItem>
              ) : devices.length === 0 ? (
                <MenuItem value="" disabled>
                  No devices available
                </MenuItem>
              ) : (
                devices.map((device) => (
                  <MenuItem key={device.id} value={device.id}>
                    {getDeviceDisplayName(device)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loadingSubmit || loadingDevices || loadingSites || !selectedSite}
            sx={{ mt: 2, alignSelf: "flex-end" }}
          >
            {loadingSubmit ? <CircularProgress size={24} /> : "Add Group"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddGroup;